<script setup lang="ts">
import { computed, ref } from 'vue';

export interface ModalFormProps {
  loading?: boolean;
  mode: 'create' | 'edit' | 'view';
  modelValue: boolean;
  primaryButtonDisabled?: boolean;
  primaryButtonText?: string;
  showActions?: boolean;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  title: string;
}

const props = withDefaults(defineProps<ModalFormProps>(), {
  loading: false,
  primaryButtonDisabled: false,
  primaryButtonText: undefined,
  showActions: true,
  size: 'lg',
});

const emit = defineEmits<{
  'cancel': [];
  'edit': [];
  'save': [];
  'update:modelValue': [value: boolean];
}>();

const isEditing = ref(false);

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const isViewMode = computed(() => props.mode === 'view' && !isEditing.value);
const isCreateMode = computed(() => props.mode === 'create');
const isEditMode = computed(() => props.mode === 'edit' || (props.mode === 'view' && isEditing.value));

const modalSizeClass = computed(() => {
  const sizeMap = {
    lg: 'modal-lg',
    md: '',
    sm: 'modal-sm',
    xl: 'modal-xl',
  };
  return sizeMap[props.size];
});

const primaryButtonText = computed(() => {
  if (props.primaryButtonText) return props.primaryButtonText;
  if (isViewMode.value) return 'Editar';
  if (isCreateMode.value) return 'Cadastrar';
  return 'Salvar';
});

const handleClose = () => {
  if (props.loading) return;
  isEditing.value = false;
  emit('update:modelValue', false);
  emit('cancel');
};

const resetEditingState = () => {
  isEditing.value = false;
};

defineExpose({
  resetEditingState,
});

const handlePrimaryAction = () => {
  if (isViewMode.value) {
    isEditing.value = true;
    emit('edit');
  } else {
    emit('save');
  }
};

const handleCancel = () => {
  if (props.loading) return;
  if (isEditing.value) {
    isEditing.value = false;
  } else {
    handleClose();
  }
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="modal modal-blur fade show"
      style="display: block;"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      @keydown.escape="handleClose"
    >
      <button
        type="button"
        class="modal-backdrop fade show"
        aria-label="Fechar"
        :disabled="loading"
        @click="handleClose"
      />

      <div class="modal-dialog modal-dialog-centered" :class="modalSizeClass" role="document">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">{{ title }}</h5>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              :disabled="loading"
              @click="handleClose"
            />
          </div>

          <div class="modal-body">
            <slot
              :is-view-mode="isViewMode"
              :is-edit-mode="isEditMode"
              :is-create-mode="isCreateMode"
              :is-loading="loading"
            />
          </div>

          <div v-if="showActions" class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="loading"
              @click="handleCancel"
            >
              {{ isEditing ? 'Cancelar Edição' : 'Cancelar' }}
            </button>

            <button
              type="button"
              class="btn btn-primary"
              :disabled="loading || primaryButtonDisabled"
              @click="handlePrimaryAction"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              {{ primaryButtonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.modal {
  z-index: 1055;

  &.show {
    display: block !important;
  }

  .modal-dialog {
    position: relative;
    z-index: 1060;
  }
}

.modal-backdrop {
  z-index: 1050;
}
</style>
